import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { APIList } from 'src/interfaces'
import { Entities } from 'src/enums'
import { EventType } from 'src/api'
import { useApiInstance } from './useApiInstance'
import {
  buildRequestParams,
  ObjectType,
  transformPage,
  transformPerPage,
} from './helpers'

interface RequestParams {
  params?: Partial<AdminEventTypesGetParams>;
  paramsTransform?: Array<(params: Record<string, any>) => Record<string, any>>;
  options?: Omit<
    UseQueryOptions<unknown, Error, APIList<EventType>>,
    'queryKey' | 'queryFn'
  >;
  enabled?: boolean
}

interface AdminEventTypesGetParams {
  limit: number;
  offset: number;
  sort: string;
}

const schema: ObjectType['schema'] = {
  limit: 'number',
  offset: 'number',
  sort: 'string',
}

export const useEventTypeListRequest = ({
  params = {},
  paramsTransform = [],
  options,
  enabled = true,
}: RequestParams = {}) => {
  const api = useApiInstance()

  const transformedParams = [
    transformPerPage,
    transformPage,
    ...paramsTransform,
  ].reduce((param, transform) => transform(param), params)

  const queryParams = buildRequestParams<AdminEventTypesGetParams>(
    transformedParams,
    schema,
  )

  return useQuery<unknown, Error, APIList<EventType>>(
    [Entities.EventTypes, JSON.stringify(transformedParams)],
    async () => {
      const {
        limit,
        offset,
        sort,
      } = queryParams
      const { data } = await api.v1AdminEventsTypesGet(
        limit,
        offset,
        sort,
      )
      return data
    },
    {
      enabled: Object.keys(transformedParams).length > 0 && enabled,
      ...options,
    },
  )
}
